import React, { useEffect, useRef, useState } from 'react';

import type { MediaManagerTemplate } from 'modules/manager/domain/interfaces';
import { useRenderTemplateMutation, type RenderTemplateArgs } from 'modules/manager/store/render-template-api';
import { RenderPreviewSkeleton } from 'modules/manager/ui/components/skeletons';
import { deriveAsyncState } from 'shared/utils/async-state';
import UDButton from 'ud-ui/components/button';
import UDAsyncContent from 'ud-ui/components/async-content';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';

import { buildDevRenderParams } from './dev-render-params';

interface RenderPreviewModalProps {
  /** Шаблон, открытый в редакторе. Рендерится его **сохранённая** версия из базы. */
  template: MediaManagerTemplate;
  onClose: () => void;
}

/**
 * Сообщение по коду ответа, а не по телу: при `responseType: 'blob'` axios отдаёт блобом
 * и тело ошибки, поэтому JSON с описанием оттуда не достать без лишнего чтения.
 */
function errorMessage(status: number | 'ABORTED' | undefined): string {
  switch (status) {
    case 403:
      return 'Нет доступа к этому шаблону. Глобальные пресеты не принадлежат организации — сначала сделайте копию.';
    case 404:
      return 'Шаблон не найден. Возможно, он удалён — перезагрузите список шаблонов.';
    case 500:
      return 'Сервер не смог собрать данные или отрисовать картинку. Проверьте, что фикстурные id ещё существуют.';
    default:
      return 'Не удалось получить картинку. Проверьте соединение и попробуйте ещё раз.';
  }
}

/**
 * Превью серверного рендера шаблона: один `POST /api/render-template`, картинка в теле
 * модалки и кнопка «Скачать».
 *
 * Модалка, а не новая вкладка с `objectURL`: у окна есть куда положить сообщение об
 * отказе (403 на чужом шаблоне — штатный ответ бэкенда), и не теряется контекст «какой
 * именно шаблон рендерили».
 *
 * Монтировать только на время показа — закрытие обязано отпустить и запрос, и объектный
 * адрес картинки.
 */
export function RenderPreviewModal({ template, onClose }: RenderPreviewModalProps) {
  const [renderTemplate, { data: blob, isLoading, isError, error }] = useRenderTemplateMutation();
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // Фоны уезжают base64, а не ссылками, поэтому их сначала надо скачать и закодировать
  // (`buildDevRenderParams` асинхронный). Пока это идёт, запроса ещё нет, и без своего
  // флага модалка мигнула бы пустым состоянием вместо загрузки.
  const [isPreparing, setIsPreparing] = useState(true);

  // Запрос уходит один раз на открытие. Триггер держим в ref: RTK Query переcоздаёт
  // его вместе с состоянием мутации, и в зависимостях эффекта он дал бы бесконечный
  // цикл «ответ → новый рендер → ответ». Кэш мутации без `fixedCacheKey` уходит вместе
  // с размонтированием, поэтому сбрасывать его в cleanup не нужно.
  const requestRef = useRef(renderTemplate);
  requestRef.current = renderTemplate;

  useEffect(() => {
    const controller = new AbortController();
    let request: ReturnType<typeof renderTemplate> | undefined;

    setIsPreparing(true);

    void (async () => {
      const args: RenderTemplateArgs = await buildDevRenderParams(template, controller.signal);

      // Модалку успели закрыть, пока качались фоны — запрос уже не нужен.
      if (controller.signal.aborted) return;

      setIsPreparing(false);
      request = requestRef.current(args);
    })();

    return () => {
      controller.abort();
      request?.abort();
    };
  }, [template]);

  // Объектный адрес живёт ровно столько, сколько показывается картинка.
  useEffect(() => {
    if (!blob) {
      setImageUrl(null);
      return;
    }

    const url = URL.createObjectURL(blob);
    setImageUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [blob]);

  const status = (error as { status?: number | 'ABORTED' } | undefined)?.status;
  // Отмена запроса — не ошибка бэкенда: она приходит на размонтировании и рисовать
  // по ней ветку ошибки не надо.
  const isRealError = isError && status !== 'ABORTED';

  // Кнопка, а не `<a download>` в футере: вложенная в ссылку китовая кнопка съедала бы
  // клик, а собственная ссылка рядом с UDButton разъезжается с ним по виду.
  const handleDownload = () => {
    if (!imageUrl) return;

    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `${template.name}.png`;
    link.click();
  };

  const state = deriveAsyncState({
    itemsCount: imageUrl ? 1 : 0,
    hasCurrentData: imageUrl != null,
    isFetching: isLoading || isPreparing,
    isError: isRealError,
  });

  return (
    <Modal show onClose={onClose} size="lg">
      <Modal.Header
        title="Рендер шаблона"
        subtitle={`«${template.name}» — рендерится сохранённая версия шаблона с тестовыми данными`}
      />

      <Modal.Body>
        <UDAsyncContent
          state={state}
          // Высота нужна веткам ошибки; загрузку держит плашка в пропорциях шаблона — картинка
          // того же размера встаёт на её место, и модалка не прыгает.
          minHeight={320}
          loading={<RenderPreviewSkeleton width={template.width} height={template.height} />}
          error={
            <UDText variant="text-2" className="text-inkSecondary px-6 text-center">
              {errorMessage(status)}
            </UDText>
          }
        >
          {imageUrl && <img src={imageUrl} alt={`Рендер шаблона «${template.name}»`} className="rounded-card mx-auto max-h-[60vh]" />}
        </UDAsyncContent>
      </Modal.Body>

      <Modal.Footer>
        <UDButton variant="secondary" onClick={onClose}>
          Закрыть
        </UDButton>
        {imageUrl && (
          <UDButton variant="primary" onClick={handleDownload}>
            Скачать
          </UDButton>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default RenderPreviewModal;
